import { createContext, useContext } from "react";
import type { Note } from "../types";

/** A note with its id, as the notes editor lists it. */
export interface NoteItem {
  id: string;
  note: Note;
}

/**
 * What the author may do with whole paths and with notes, provided by the builder to the path cards, the canvas tiles
 * and the notes editors, so each of them asks for the action it needs without the shell passing every handler down.
 */
export interface BuilderTools {
  /** The author may change the workflow; a viewer can still read its notes. */
  editable: boolean;
  /** There is room for one more path (a copy needs it). */
  canAddPath: boolean;
  /** How many connections from other paths lead to the path: what deleting it clears. */
  incomingCount: (pathId: string) => number;
  rename: (pathId: string, label: string) => void;
  duplicate: (pathId: string) => void;
  remove: (pathId: string) => void;
  /** The notes of a path, or of the workflow for `null`. */
  notesAt: (pathId: string | null) => NoteItem[];
  canAddNote: boolean;
  addNote: (pathId: string | null, text: string) => string | null;
  editNote: (id: string, text: string) => void;
  removeNote: (id: string) => void;
}

export const BuilderToolsContext = createContext<BuilderTools | null>(null);

/** The path and note actions of the builder, or `null` where the workflow cannot be edited. */
export const useBuilderTools = (): BuilderTools | null => useContext(BuilderToolsContext);
